import { useCallback, useEffect, useMemo } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { formatDateTime } from 'utils/date-helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCalendarDay,
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { AssignmentRequestModel, GoSupportRequestFilters } from 'api/model';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useTranslations from 'contexts/basicData/useTranslations';
import { useTransportLogisticsGetRejectedAndNotHandledAssignmentRequests } from 'api/transport-logistics/transport-logistics';
import { makeDateComparator, makeStringComparator } from 'utils/sorting';
import { useInterval } from 'usehooks-ts';
import TextButton from 'components/inputs/TextButton';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import useModalStack from 'contexts/modal/useModalStack';
import AssignmentRequestModalWrapper from 'pages/InternalDeliveryGroup/Assignments/components/AssignmentRequestModalWrapper';
import {
  Centered,
  getTableRowClassName,
} from 'components/Dashboard/table-helpers';

const TABLE_NAME = 'placeholder-assignments';

const RejectedOrUnapprovedAssignmentRequestsWidget = () => {
  const modalStack = useModalStack();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();
  const translations = useTranslations();

  const filters: GoSupportRequestFilters = {
    startDate: daterange[0] ?? new Date(),
    endDate: daterange[1] ?? new Date(),
    businessUnitIDs: selectedBusinessUnits,
    includedAssigneeIDs: [],
  };

  // api
  const getDataCall =
    useTransportLogisticsGetRejectedAndNotHandledAssignmentRequests();

  const getEvents = async () => {
    try {
      await getDataCall.mutateAsync({
        data: filters,
      });
    } catch (error) {
      // console.error('Error fetching events:', error);
    }
  };

  useInterval(() => {
    getEvents();
  }, UPDATE_INTERVAL);

  // Fetch events on mount and when filters change
  useEffect(() => {
    getEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daterange[0], daterange[1], selectedBusinessUnits]);

  const filteredEvents = useMemo(() => {
    if (!searchText.length) {
      return getDataCall.data ?? [];
    }
    return (
      getDataCall.data?.filter((e) => {
        return (
          e.requestedFor?.name
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (e.requestedFor?.name ?? '')
            .toLowerCase()
            .includes(searchText.toLowerCase())
        );
      }) ?? []
    );
  }, [getDataCall.data, searchText]);

  const openAssignmentRequest = useCallback(
    (id: number) => {
      modalStack.push(
        <AssignmentRequestModalWrapper
          requestId={id}
          onClose={() => modalStack.pop()}
        />
      );
    },
    [modalStack]
  );

  const columnSettings: ColumnSetting<AssignmentRequestModel, any>[] =
    useMemo(() => {
      const columns: ColumnSetting<AssignmentRequestModel, any>[] = [
        {
          head: 'Nr',
          render: (e, t, f, i) => i + 1,
          width: 15,
        },
        {
          head: 'Status',
          render: (row) =>
            `${
              translations.assignmentRequestStatuses[
                row.assignmentRequestStatusID
              ]
                ? translations.assignmentRequestStatuses[
                    row.assignmentRequestStatusID
                  ]
                : ''
            }`,
          width: 50,
          sortFunction: makeStringComparator(
            (row) =>
              translations.assignmentRequestStatuses[
                row.assignmentRequestStatusID
              ] ?? ''
          ),
        },
        {
          head: 'Önskad starttid',
          render: (row) =>
            row.targetStartTime
              ? `${formatDateTime(row.targetStartTime)}`
              : '-',
          width: 100,
          sortFunction: makeDateComparator((row) => row.targetStartTime),
        },
        {
          head: 'Fälttestare',
          render: (row) => {
            return <span>{row.requestedFor?.name ?? ''}</span>;
          },
          width: 100,
          sortFunction: makeStringComparator(
            (row) => row.requestedFor?.name ?? ''
          ),
        },
        {
          head: 'Telefonnummer',
          render: (row) => {
            return <span>{row.requestedFor?.phoneNumber ?? ''}</span>;
          },
          width: 100,
          sortFunction: makeStringComparator(
            (row) => row.requestedFor?.phoneNumber ?? ''
          ),
        },
        {
          head: ' ',
          render: (row) =>
            row.requestedForID ? (
              <Centered>
                <TextButton style={{ padding: 0 }}>
                  <FontAwesomeIcon
                    onClick={() => {
                      modalStack.push(
                        <AvailabilityFormModal
                          userId={row.requestedForID!}
                          initialDate={row.targetStartTime}
                          name={row.requestedFor?.name ?? ''}
                          onClose={() => modalStack.pop()}
                          onSaved={() => modalStack.pop()}
                          viewOnly
                          showUserFields
                        />
                      );
                    }}
                    icon={faCalendarDay}
                    fontSize="18px"
                    style={{ cursor: 'pointer' }}
                  />
                </TextButton>
              </Centered>
            ) : null,
          width: 20,
        },
        {
          head: ' ',
          render: (row) => (
            <Centered>
              <FontAwesomeIcon
                onClick={() => {
                  openAssignmentRequest(row.requestID);
                }}
                icon={faArrowUpRightFromSquare}
                fontSize="18px"
                style={{ cursor: 'pointer' }}
              />
            </Centered>
          ),
          width: 20,
        },
      ];
      return columns;
    }, [
      modalStack,
      openAssignmentRequest,
      translations.assignmentRequestStatuses,
    ]);

  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.RejectedOrUnapprovedAssignmentRequests]}
      count={filteredEvents.length}
      isLoading={getDataCall.isLoading}
      info="Utfyllnadsuppdrag som behöver ersättas med faktiska uppdrag"
    >
      <Table
        columnSettings={columnSettings}
        rows={filteredEvents}
        rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.requestID)}
        useColumnWidthAsFlexAmount
      />
    </Widget>
  );
};

export default RejectedOrUnapprovedAssignmentRequestsWidget;
